import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import { useAuth } from './AuthContext';

export type AppShellSkin = 'ide' | 'yolo';

interface AppShellContextValue {
  skin: AppShellSkin;
  setSkin: (skin: AppShellSkin) => void;
  chefPanelVisible: boolean;
  setChefPanelVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

const STORAGE_KEY = 'appShellSkin';

const AppShellContext = createContext<AppShellContextValue | undefined>(undefined);

export const AppShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, token } = useAuth();
  const [skin, setSkin] = useState<AppShellSkin>('yolo');
  const [chefPanelVisible, setChefPanelVisibleState] = useState(false);
  const chefPanelVisibleRef = useRef(false);
  const preferenceWriteQueue = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    const visible = currentUser?.chefPanelVisible ?? false;
    chefPanelVisibleRef.current = visible;
    setChefPanelVisibleState(visible);
  }, [currentUser?.id, currentUser?.chefPanelVisible]);

  const setChefPanelVisible: React.Dispatch<React.SetStateAction<boolean>> = useCallback(action => {
    const visible = typeof action === 'function' ? action(chefPanelVisibleRef.current) : action;
    chefPanelVisibleRef.current = visible;
    setChefPanelVisibleState(visible);

    if (!token || !currentUser) return;
    preferenceWriteQueue.current = preferenceWriteQueue.current
      .catch(() => undefined)
      .then(async () => {
        const response = await fetch(`${API_BASE_URL}/auth/me/preferences`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ chefPanelVisible: visible }),
        });
        if (!response.ok) throw new Error('Could not save Chef panel preference');
      })
      .catch(error => console.error(error));
  }, [currentUser, token]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, skin);
  }, [skin]);

  return (
    <AppShellContext.Provider value={{ skin, setSkin, chefPanelVisible, setChefPanelVisible }}>
      {children}
    </AppShellContext.Provider>
  );
};

export const useAppShell = (): AppShellContextValue => {
  const context = useContext(AppShellContext);
  if (!context) throw new Error('useAppShell must be used within an AppShellProvider');
  return context;
};
